/**
 * Validation and compression of images chosen for personalised symbols.
 * Images are resized in the browser and stored as small JPEG/PNG data URLs,
 * so large photos never reach the server.
 */
import { LIMITS } from '@shared/constants';

export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];

export class ImageError extends Error {}

export function validateImageFile(file: { type: string; size: number }): void {
  if (!file.type.startsWith('image/')) throw new ImageError('Escolha um arquivo de imagem (JPG, PNG ou WebP).');
  if (file.size > LIMITS.maxUploadBytes) {
    throw new ImageError(`A imagem é muito grande (máximo ${Math.round(LIMITS.maxUploadBytes / 1024 / 1024)} MB).`);
  }
}

/** Target size keeping the aspect ratio, never upscaling. */
export function fitWithin(width: number, height: number, max: number): { width: number; height: number } {
  const ratio = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * ratio)), height: Math.max(1, Math.round(height * ratio)) };
}

async function loadBitmap(file: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close(): void }> {
  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch {
      /* fall back to <img> (e.g. formats createImageBitmap rejects) */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch {
    URL.revokeObjectURL(url);
    throw new ImageError('Não foi possível abrir esta imagem. Tente outra foto.');
  }
}

/** Resizes and compresses; lowers quality until it fits the size limit. */
export async function compressImage(file: File): Promise<string> {
  validateImageFile(file);
  const bitmap = await loadBitmap(file);
  try {
    let max: number = LIMITS.imageMaxDimension;
    for (let attempt = 0; attempt < 4; attempt++) {
      const { width, height } = fitWithin(bitmap.width, bitmap.height, max);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new ImageError('Seu navegador não conseguiu processar a imagem.');
      // White background so transparent PNGs look right as JPEG.
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(bitmap.source, 0, 0, width, height);
      for (const quality of [0.82, 0.7, 0.55]) {
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        if (dataUrl.length <= LIMITS.maxImageDataUrlLength) return dataUrl;
      }
      max = Math.round(max * 0.7);
    }
    throw new ImageError('Não foi possível reduzir a imagem o suficiente. Tente outra foto.');
  } finally {
    bitmap.close();
  }
}
